/**
 * Inline video/post preview embed resolution.
 *
 * Given a post's platform `type`, its canonical `url`, and an optional native
 * `systemId`, returns an iframe-embeddable player URL plus an aspect hint — or
 * `null` when the platform/type has no reliable public iframe embed (so callers
 * can simply omit the preview control rather than render a broken frame).
 *
 * The canonical post URL is the primary source (stat cards always carry it as
 * the title link); `systemId` is only a fallback and mainly helps YouTube,
 * where it is the raw 11-char video id.
 *
 * Verified embeddable: YouTube (video/stream), Twitch (VOD/clip), Bilibili
 * (video), TikTok (video), Instagram (post/reel). Facebook, Douyin, CHZZK,
 * SOOP/Afreeca, Discord, Twitter, community posts and live streams have no
 * reliable public iframe embed and return null.
 */

export type EmbedAspect = 'landscape' | 'portrait';

export interface VideoEmbed {
  url: string;
  aspect: EmbedAspect;
}

export interface VideoEmbedInput {
  type?: string;
  url?: string;
  systemId?: string;
}

/** Current page hostname — Twitch's player rejects a non-matching `parent`. */
function currentParent(): string {
  if (typeof window === 'undefined') return 'localhost';
  return window.location.hostname || 'localhost';
}

/** Pull an 11-char-ish YouTube video id out of any watch/short/live/embed URL. */
function youtubeId(url?: string, systemId?: string): string | null {
  if (url) {
    const m = url.match(
      /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/|live\/)|youtu\.be\/)([\w-]{6,})/,
    );
    if (m) return m[1];
  }
  return systemId || null;
}

export function getVideoEmbedUrl({ type, url, systemId }: VideoEmbedInput): VideoEmbed | null {
  const t = type ?? '';

  // YouTube video / stream → standard embed player.
  if (t === 'youtube_video' || t === 'youtube_stream') {
    const id = youtubeId(url, systemId);
    return id ? { url: `https://www.youtube.com/embed/${id}`, aspect: 'landscape' } : null;
  }

  // Twitch VOD → player.twitch.tv (?video=), requires a matching parent host.
  if (t === 'twitch_video') {
    const id = url?.match(/twitch\.tv\/videos\/(\d+)/)?.[1] ?? (systemId || null);
    return id
      ? {
          url: `https://player.twitch.tv/?video=${id}&parent=${currentParent()}&autoplay=false`,
          aspect: 'landscape',
        }
      : null;
  }

  // Twitch clip → clips.twitch.tv/embed (?clip=slug).
  if (t === 'twitch_clip') {
    const slug =
      url?.match(/clips\.twitch\.tv\/([^/?#]+)/)?.[1] ??
      url?.match(/twitch\.tv\/[^/]+\/clip\/([^/?#]+)/)?.[1] ??
      (systemId || null);
    return slug
      ? {
          url: `https://clips.twitch.tv/embed?clip=${slug}&parent=${currentParent()}&autoplay=false`,
          aspect: 'landscape',
        }
      : null;
  }

  // Bilibili video → player.bilibili.com; BV-form vs numeric aid change the param.
  if (t === 'bilibili_video') {
    const raw = url?.match(/bilibili\.com\/video\/([^/?#]+)/)?.[1] ?? (systemId || null);
    if (!raw) return null;
    const param = /^BV/i.test(raw) ? `bvid=${raw}` : `aid=${raw.replace(/^av/i, '')}`;
    return { url: `https://player.bilibili.com/player.html?${param}&autoplay=0`, aspect: 'landscape' };
  }

  // TikTok video → official embed player (vertical).
  if (t === 'tiktok_video') {
    const id =
      url?.match(/tiktok\.com\/(?:@[^/]+\/video|embed\/v2|player\/v1)\/(\d+)/)?.[1] ?? (systemId || null);
    return id ? { url: `https://www.tiktok.com/embed/v2/${id}`, aspect: 'portrait' } : null;
  }

  // Instagram post/reel → official /embed/ (vertical). Handles /p/, /reel/, /reels/, /tv/.
  if (t === 'instagram_video' || t === 'instagram_photo') {
    const code = url?.match(/instagram\.com\/(?:p|reel|reels|tv)\/([^/?#]+)/)?.[1] ?? null;
    return code ? { url: `https://www.instagram.com/p/${code}/embed/`, aspect: 'portrait' } : null;
  }

  return null;
}
